/**
 * תבניות — Meta template status per registry template (moved here from
 * Tools → WhatsApp). "סנכרן תבניות" creates missing ones in Meta.
 * Error codes: CK-10505 (sync).
 */
import { useMemo, useState } from "react";
import { ArrowsClockwise, Warning, FileText } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import { useToast } from "../../../../contexts/ToastContext";
import { useGetWhatsAppStatusQuery, useRunWhatsAppActionMutation } from "../../../../store/services/nivApi";
import { MESSAGE_TYPES, MESSAGE_TYPE_IDS, GROUP_LABELS, META_STATUS_LABELS } from "./messagesShared";

const FILTERS = [
  { id: "all", label: "הכל" },
  { id: "APPROVED", label: "מאושרות", tone: "ok" },
  { id: "PENDING", label: "בבדיקה", tone: "warn" },
  { id: "bad", label: "נדחו / מושהות", tone: "bad" },
  { id: "MISSING", label: "לא ב-Meta", tone: "muted" },
];

export default function TemplatesPanel({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const { data: status, isLoading, isFetching, refetch } = useGetWhatsAppStatusQuery(undefined, { skip: !isAuthorized });
  const [runAction, { isLoading: acting }] = useRunWhatsAppActionMutation();
  const [filter, setFilter] = useState("all");

  const typesByTemplate = useMemo(() => {
    const m = {};
    MESSAGE_TYPE_IDS.forEach((id) => {
      const w = MESSAGE_TYPES[id].waTemplateId;
      if (w) (m[w] = m[w] || []).push(id);
    });
    return m;
  }, []);

  const templates = useMemo(() => {
    const list = status?.templates || [];
    return list.filter((t) => {
      if (filter === "all") return true;
      if (filter === "bad") return ["REJECTED", "PAUSED", "DISABLED"].includes(t.metaStatus);
      return t.metaStatus === filter;
    });
  }, [status, filter]);

  const handleSync = async () => {
    try {
      const res = await runAction({ action: "syncTemplates" }).unwrap();
      const created = (res?.results || []).filter((r) => r.action === "created").length;
      const failed = (res?.results || []).filter((r) => r.action === "failed");
      if (failed.length) showError(`סנכרון הושלם עם ${failed.length} כשלונות: ${failed.map((f) => f.name).join(", ")}`);
      else showSuccess(created ? `נוצרו ${created} תבניות — ממתינות לאישור Meta` : "כל התבניות כבר קיימות ב-Meta");
    } catch (error) {
      console.error("[CK-10505] WhatsApp template sync failed:", {
        errorCode: "CK-10505",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("סנכרון התבניות נכשל");
    }
  };

  const counts = useMemo(() => {
    const c = { APPROVED: 0, PENDING: 0, bad: 0, MISSING: 0 };
    (status?.templates || []).forEach((t) => {
      if (["REJECTED", "PAUSED", "DISABLED"].includes(t.metaStatus)) c.bad += 1;
      else if (c[t.metaStatus] !== undefined) c[t.metaStatus] += 1;
    });
    return c;
  }, [status]);

  return (
    <div className="nmsg-panel">
      <div className="nmsg-toolbar">
        <div className="nmsg-filters" role="group" aria-label="סטטוס">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={`nmsg-filter ${filter === f.id ? "active" : ""} ${f.tone ? `tone-${f.tone}` : ""}`} onClick={() => setFilter(f.id)}>
              {f.label}
              {f.id !== "all" && <span className="nmsg-count">{counts[f.id] || 0}</span>}
            </button>
          ))}
        </div>
        <div className="nmsg-card-side">
          <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => refetch()} disabled={isFetching}>
            <ArrowsClockwise size={14} className={isFetching ? "spinning" : ""} /> רענון
          </button>
          <button type="button" className="niv-btn niv-btn-primary niv-btn-sm" onClick={handleSync} disabled={acting || !status?.channels?.users?.configured}>
            {acting ? <LoaderLoop size="xs" /> : <ArrowsClockwise size={14} />} סנכרן תבניות ל-Meta
          </button>
        </div>
      </div>
      {status?.templatesError && (
        <div className="nmsg-notice nmsg-notice-bad">
          <Warning size={16} weight="fill" /> לא ניתן לקרוא תבניות מ-Meta: {status.templatesError}
        </div>
      )}

      {isLoading ? (
        <ContentLoader size="sm" compact message="טוען תבניות…" />
      ) : templates.length === 0 ? (
        <div className="nmsg-empty">
          <FileText size={36} weight="light" />
          <p>אין תבניות בסינון הזה.</p>
        </div>
      ) : (
        <div className="nmsg-grid">
          {templates.map((t) => {
            const s = META_STATUS_LABELS[t.metaStatus] || { label: t.metaStatus, tone: "warn" };
            const usedBy = typesByTemplate[t.id] || [];
            const smsOnlyNow = t.metaStatus !== "APPROVED" || t.enabled === false;
            return (
              <article key={t.id} className={`nmsg-card nmsg-template ${smsOnlyNow ? "is-sms-only" : ""}`}>
                <header className="nmsg-card-head">
                  <div className="nmsg-card-titles">
                    <span className="nmsg-eyebrow">{GROUP_LABELS[t.channel] || t.channel} · {t.category} · <span dir="ltr">{t.language}</span></span>
                    <h4 className="nmsg-card-title" dir="ltr">{t.name}</h4>
                  </div>
                  <div className="nmsg-card-side">
                    <span className={`nmsg-chip nmsg-chip-${s.tone}`}>{s.label}</span>
                    {t.enabled === false && <span className="nmsg-chip nmsg-chip-muted">כבויה ברישום</span>}
                    {smsOnlyNow && usedBy.length > 0 && <span className="nmsg-chip nmsg-chip-fallback">SMS בלבד כרגע</span>}
                  </div>
                </header>
                <pre className="nmsg-template-body" dir="auto">{t.body}</pre>
                {t.footer && <p className="nmsg-template-footer">{t.footer}</p>}
                {t.buttons?.length > 0 && (
                  <div className="nmsg-template-buttons">
                    {t.buttons.map((b, i) => (
                      <span key={i} className={`nmsg-chip ${b.type === "URL" ? "nmsg-chip-sms" : "nmsg-chip-whatsapp"}`} title={b.url || ""}>
                        {b.text}
                      </span>
                    ))}
                  </div>
                )}
                <div className="nmsg-row-meta">
                  {t.smsTemplateId ? <span dir="ltr">SMS twin: {t.smsTemplateId}</span> : <span>SMS: טקסט מהקורא</span>}
                  {usedBy.length > 0 && <span>משמש: {usedBy.map((id) => MESSAGE_TYPES[id].label).join(", ")}</span>}
                  {t.qualityScore && <span>איכות: {t.qualityScore}</span>}
                </div>
                {t.rejectedReason && (
                  <p className="nmsg-bad nmsg-help">
                    <Warning size={13} weight="fill" /> סיבת דחייה: {t.rejectedReason}
                  </p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
